import {
  ApiLoginRequest,
  ApiLoginResponse,
  ApiPermissionEntry,
} from "../entities/ApiAuthData";
import AuthData, { EffectivePermission } from "../entities/AuthData";

interface JwtPayload {
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

export class AuthDataMapper {
  /**
   * Transforms internal login data to API request format
   */
  static toApiLoginRequest(email: string, password: string): ApiLoginRequest {
    return {
      login: email, // Map email to login field as per API spec
      password: password,
    };
  }

  /**
   * Transforms API login response to internal AuthData format
   */
  static fromApiLoginResponse(
    login: string,
    apiResponse: ApiLoginResponse,
  ): AuthData {
    const jwtPayload = this.decodeJwtPayload(apiResponse.token);

    const businessRoleLabels = Array.isArray(apiResponse.roles)
      ? apiResponse.roles
      : [];
    // /auth/signin's `roles` field only lists roles published as RBAC
    // catalog rows (Role entities) — derived from Keycloak GROUP membership
    // (KeycloakRepositoryAdapter#findByEmailOrLogin walks userResource.groups()
    // only). A persona assigned as a bare Keycloak realm role, never a group
    // member (BOX_AFRICA_ADMIN — see KeycloakRoleInitializer#assignBoxAfricaAdminRole,
    // confirmed empty `roles: []` from signin despite realm_access.roles
    // carrying it), never appears there. The JWT itself always carries the
    // real grant, so read it from there too rather than relying solely on
    // the backend-published, group-derived list.
    const realmRoleLabels = Array.isArray(jwtPayload?.realm_access?.roles)
      ? jwtPayload?.realm_access?.roles
      : [];
    const roleLabels = Array.from(
      new Set([...businessRoleLabels, ...realmRoleLabels]),
    );

    // Same gap, one level down: `permissions` is built server-side from the
    // same group-derived roles (AuthController#authenticateUser iterates
    // userCredentials.getRoles()), so it inherits the exact same blind spot.
    // resource_access carries the same tuples in "<CLIENT>": ["ACTION:RESOURCE", ...]
    // form — client name is the domain, the client-role name splits on ':'.
    const jwtPermissions = this.decodeJwtResourcePermissions(jwtPayload);
    const effectivePermissions = this.dedupePermissions([
      ...this.mapEffectivePermissions(apiResponse.permissions),
      ...jwtPermissions,
    ]);

    return {
      id: apiResponse.id || "0", // Default ID if not provided
      firstName: apiResponse.firstName,
      lastName: apiResponse.lastName,
      login: login,
      email: apiResponse.email,
      roles: [],
      roleLabels,
      token: apiResponse.token ?? null,
      effectivePermissions,
      customerCode: apiResponse.customerCode ?? null,
      firstLogin: apiResponse.fistLogin ?? false,
      refreshToken: apiResponse.refreshToken,
      expiresIn: apiResponse.expiresIn,
      refreshExpiresIn: apiResponse.refreshExpiresIn,
      habilite: apiResponse.habilite === true,
    };
  }

  /**
   * Decodes a JWT's payload without verifying the signature: display/gating only, never a trust
   * boundary (the backend re-checks every call against the same token).
   */
  private static decodeJwtPayload(
    token: string | null | undefined,
  ): JwtPayload | null {
    if (!token) return null;
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) return null;
    try {
      const base64 = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
      const json = atob(
        base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "="),
      );
      return JSON.parse(json) as JwtPayload;
    } catch {
      return null;
    }
  }

  /**
   * `resource_access` carries the same permission tuples the backend derives from group
   * membership, keyed as `{ "<CLIENT>": ["ACTION:RESOURCE", ...] }` — client name is the domain,
   * the client-role name splits on ':'. Roles with no ':' (Keycloak's built-in `account` client:
   * manage-account, view-profile...) are not permission tuples and are skipped.
   */
  private static decodeJwtResourcePermissions(
    jwtPayload: JwtPayload | null,
  ): EffectivePermission[] {
    const resourceAccess = jwtPayload?.resource_access;
    if (!resourceAccess) return [];

    return Object.entries(resourceAccess).flatMap(([client, access]) => {
      if (!Array.isArray(access?.roles)) return [];
      return access.roles
        .map((role) => role.split(":"))
        .filter((parts): parts is [string, string] => parts.length === 2)
        .map(([action, resource]) => ({
          domain: client.trim().toUpperCase(),
          action: action.trim().toUpperCase(),
          resource: resource.trim().toUpperCase(),
        }));
    });
  }

  private static dedupePermissions(
    permissions: EffectivePermission[],
  ): EffectivePermission[] {
    const seen = new Set<string>();
    const result: EffectivePermission[] = [];
    for (const p of permissions) {
      const key = `${p.domain}|${p.action}|${p.resource}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(p);
      }
    }
    return result;
  }

  private static mapEffectivePermissions(
    permissions: ApiPermissionEntry[] | undefined,
  ): EffectivePermission[] {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return [];
    }

    return permissions
      .filter(
        (p) =>
          typeof p?.domaine === "string" &&
          typeof p?.action === "string" &&
          typeof p?.ressource === "string",
      )
      .map((p) => ({
        domain: (p.domaine as string).trim().toUpperCase(),
        action: (p.action as string).trim().toUpperCase(),
        resource: (p.ressource as string).trim().toUpperCase(),
      }))
      .filter((p) => p.domain !== "" && p.action !== "" && p.resource !== "");
  }
}
